import { useLayoutEffect } from 'react'

/**
 * Sync a CSS custom property with the live DashboardHeader height so any fixed-
 * position element anchored beneath the header tracks its bottom edge through
 * reflows (font load, search wrap, viewport rotation, breakpoint changes).
 *
 * The DashboardHeader reflows into multiple rows on narrow viewports — at 400px
 * it measures ~251px; at 820px ~153px; at desktop widths ~80px — so a static
 * CSS value cannot anchor a sidebar or mobile bar beneath it. Setting a CSS var
 * keeps both surfaces in sync via shared variable consumption.
 *
 * Looks for the header at `#DashboardHeaderComponent` first, then falls back to
 * `#main-content > header`. The consumer mounts at the Router root and outlives
 * DashboardLayout, so each route change can replace the header element; a
 * MutationObserver re-attaches the ResizeObserver whenever the observed header
 * leaves the DOM (or before it first appears).
 *
 * useLayoutEffect runs before paint so the consumer's first render lands in the
 * right place.
 *
 * @param {string} [varName='--sidebar-slider-header-bottom'] - CSS custom
 *   property to set on `document.documentElement`. Consumers should generally
 *   accept the default — the shell's SCSS and the LL feature's SCSS both
 *   reference this var name.
 */
function useHeaderOffset(varName = '--sidebar-slider-header-bottom') {
  useLayoutEffect(() => {
    let ro = null
    let current = null

    const findHeader = () =>
      document.querySelector('#DashboardHeaderComponent') || document.querySelector('#main-content > header')

    const apply = el => {
      const h = el.getBoundingClientRect().height
      if (h > 0) {
        document.documentElement.style.setProperty(varName, `${Math.round(h)}px`)
      }
    }

    const attach = () => {
      if (current && current.isConnected) return
      const el = findHeader()
      if (el === current) return
      if (ro) ro.disconnect()
      ro = null
      current = el
      if (!el) return
      apply(el)
      if ('ResizeObserver' in window) {
        ro = new ResizeObserver(() => apply(el))
        ro.observe(el)
      }
    }

    attach()
    const mo = new MutationObserver(attach)
    mo.observe(document.body, { childList: true, subtree: true })

    return () => {
      if (ro) ro.disconnect()
      mo.disconnect()
      document.documentElement.style.removeProperty(varName)
    }
  }, [varName])
}

export default useHeaderOffset
